import { distanceInMeters } from "../road-graph.mjs";

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

export function coordinateInReveal(coordinate, reveal, bbox = null) {
  if (bbox
    && (coordinate[0] < bbox.west
      || coordinate[0] > bbox.east
      || coordinate[1] < bbox.south
      || coordinate[1] > bbox.north)) {
    return false;
  }
  if (Array.isArray(reveal.coordinates) && reveal.coordinates.length >= 2) {
    return distanceToPolylineMeters(coordinate, reveal.coordinates) <= reveal.radiusMeters;
  }
  return distanceInMeters(coordinate, reveal.coordinate) <= reveal.radiusMeters;
}

export function distanceToPolylineMeters(point, coordinates) {
  return coordinates.slice(1).reduce((nearest, coordinate, index) => (
    Math.min(nearest, distanceToSegmentMeters(point, coordinates[index], coordinate))
  ), Infinity);
}

export function distanceToSegmentMeters(point, start, end) {
  const latitude = (point[1] + start[1] + end[1]) / 3 * Math.PI / 180;
  const metersPerLongitude = Math.cos(latitude) * Math.PI * 6371000 / 180;
  const metersPerLatitude = Math.PI * 6371000 / 180;
  const toLocal = (coordinate) => [
    (coordinate[0] - start[0]) * metersPerLongitude,
    (coordinate[1] - start[1]) * metersPerLatitude,
  ];
  const localPoint = toLocal(point);
  const localEnd = toLocal(end);
  const denominator = localEnd[0] ** 2 + localEnd[1] ** 2;
  const fraction = denominator
    ? clamp((localPoint[0] * localEnd[0] + localPoint[1] * localEnd[1]) / denominator, 0, 1)
    : 0;
  const deltaX = localPoint[0] - localEnd[0] * fraction;
  const deltaY = localPoint[1] - localEnd[1] * fraction;
  return Math.hypot(deltaX, deltaY);
}

// Radius-padded bounding box that is a strict superset of the reveal's reach, so a
// coordinate outside the box is guaranteed to be outside the reveal. Longitude padding
// uses the largest |latitude| in the reveal so it stays conservative (wider) everywhere.
export function revealBoundingBox(reveal) {
  const coordinates = Array.isArray(reveal.coordinates) && reveal.coordinates.length >= 2
    ? reveal.coordinates
    : [reveal.coordinate];
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  let maxAbsLatitude = 0;
  for (const coordinate of coordinates) {
    const [longitude, latitude] = coordinate;
    if (longitude < west) west = longitude;
    if (longitude > east) east = longitude;
    if (latitude < south) south = latitude;
    if (latitude > north) north = latitude;
    maxAbsLatitude = Math.max(maxAbsLatitude, Math.abs(latitude));
  }
  const radiusMeters = reveal.radiusMeters || 0;
  const latPad = radiusMeters / 111320;
  const maxAbsLatRadians = maxAbsLatitude * Math.PI / 180;
  const lonPad = radiusMeters / (Math.max(Math.cos(maxAbsLatRadians), 0.01) * 111320);
  return {
    west: west - lonPad,
    south: south - latPad,
    east: east + lonPad,
    north: north + latPad,
  };
}
